// Copyright ©️ 2026 eVolve MEP, LLC

import { useState } from 'react';

import {
  useEvolvePaginatedGet,
  useEvolvePost,
  type TaskType,
  type TaskTypeGroup,
  type WorkOrderId,
  type WorkOrderItem,
} from '@evolvemep/api-ts';
import { Button, Flex, Text } from '@mantine/core';
import { useForm } from '@mantine/form';
import { notifications } from '@mantine/notifications';

import { EvolveIcon } from 'assets/icons/EvolveIcon';
import { AgGridStyleTooltip } from 'components/Mantine/AgGridStyleTooltip';
import { FormPopover, useFormPopover } from 'components/Mantine/Popovers/FormPopover';
import { SearchableCheckList } from 'components/Mantine/TypeSafeSelect/PaginatedSelectors/SearchableCheckList';

type Props = {
  taskTypes: TaskType[];
  taskTypeGroups: TaskTypeGroup[];
  workOrderId: WorkOrderId;
  onTasksAdded: () => void;
};

type FormValues = {
  selectAll: boolean;
  items: WorkOrderItem[];
};

const AddTaskFields = ({ taskTypes, taskTypeGroups, workOrderId, onTasksAdded }: Props) => {
  const { apiCall: addTasksToBillOfProcess } = useEvolvePost('shop/task');
  const paginatedGet = useEvolvePaginatedGet('shop/workOrder/item', {
    defaultConfig: {
      params: {
        workOrderId,
        orderBy: 'name:asc',
      },
    },
  });
  const form = useForm<FormValues>({
    initialValues: {
      selectAll: false,
      items: [],
    },
    validate: {
      items: (items, values) => !values.selectAll && items.length === 0,
    },
  });

  const onSubmit = async ({ selectAll, items }: typeof form.values) => {
    await addTasksToBillOfProcess({
      taskTypeIds: taskTypes.map((t) => t.taskTypeId),
      taskTypeGroupIds: taskTypeGroups.map((g) => g.taskTypeGroupId),
      ...(selectAll ? { workOrderId } : { workOrderItemIds: items.map((s) => s.workOrderItemId) }),
    });
    notifications.show({
      title: 'Successfully added',
      message: selectAll
        ? 'Selected tasks added successfully to all items'
        : `Selected tasks added successfully to ${items.length} item${items.length === 1 ? '' : 's'}`,
      color: 'green',
    });
    onTasksAdded();
  };

  useFormPopover(form, onSubmit);

  return (
    <>
      <Text size="sm" mb="xs">
        Select items to map selected task{taskTypes.length === 1 ? '' : 's'}.
      </Text>
      <SearchableCheckList
        paginatedGet={paginatedGet}
        searchKey="name"
        getItemLabel={({ name }) => name}
        idKey="workOrderItemId"
        value={form.values.items}
        onChange={(items) => form.setFieldValue('items', items)}
        textInputProps={{ size: 'xs', placeholder: 'Search items...', style: { width: '100%' } }}
        flexListProps={{
          gap: 'xs',
          style: {
            maxHeight: 200,
          },
        }}
        autoSelectWhenOnlyOneOption
        selectAllState={[
          form.values.selectAll,
          (v) => form.setFieldValue('selectAll', typeof v === 'function' ? v(form.values.selectAll) : v),
        ]}
        nothingFoundMessage={
          <Flex gap={5} align="center">
            No write-in items found.
            <AgGridStyleTooltip
              withArrow
              label={
                <Flex gap={5} align="center">
                  <Text>Tasks cannot be mapped to assemblies, denoted via</Text>
                  <EvolveIcon icon="Assembly" size="sm" />
                </Flex>
              }
            >
              <EvolveIcon icon="Info" size="sm" color="primary" />
            </AgGridStyleTooltip>
          </Flex>
        }
      />
    </>
  );
};

export const AddTaskPopover = (props: Props) => {
  const { taskTypes, taskTypeGroups } = props;

  const [opened, setOpened] = useState(false);

  return (
    <FormPopover
      width={300}
      opened={opened}
      onClose={() => setOpened(false)}
      confirmationText="Add"
      target={
        <Button
          onClick={() => setOpened((o) => !o)}
          leftSection={<EvolveIcon size="sm" icon="Add" />}
          disabled={taskTypes.length === 0 && taskTypeGroups.length === 0}
          variant="filled"
        >
          Add tasks to items
        </Button>
      }
    >
      <AddTaskFields {...props} />
    </FormPopover>
  );
};
